import React, { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { TrendingUp } from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BacktestChart = ({ navData, benchmarkData, drawdownData }) => {
  const chartRef = useRef(null);

  if (!navData || navData.length === 0) {
    return null;
  }

  const labels = navData.map(item => item.date);
  const portfolioValues = navData.map(item => item.value);
  const benchmarkValues = benchmarkData ? benchmarkData.map(item => item.value) : [];
  const drawdowns = drawdownData || navData.map(item => item.drawdown || 0);

  // Main NAV Chart Data
  const navChartData = {
    labels: labels,
    datasets: [
      {
        label: 'Portfolio NAV',
        data: portfolioValues,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      ...(benchmarkValues.length > 0 ? [{
        label: 'S&P 500 Benchmark',
        data: benchmarkValues,
        borderColor: 'rgba(156, 163, 175, 1)',
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      }] : [])
    ],
  };

  // Drawdown Chart Data
  const drawdownChartData = {
    labels: labels,
    datasets: [
      {
        label: 'Drawdown',
        data: drawdowns,
        borderColor: 'rgba(239, 68, 68, 1)',
        backgroundColor: 'rgba(239, 68, 68, 0.3)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };

  const navOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 13,
            weight: '500'
          }
        },
      },
      title: {
        display: true,
        text: 'Portfolio Equity Curve',
        color: '#fff',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += '$' + context.parsed.y.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#94a3b8',
          maxTicksLimit: 8,
          font: {
            size: 11
          }
        }
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
        },
        ticks: {
          color: '#94a3b8',
          callback: function(value) {
            return '$' + value.toLocaleString();
          },
          font: {
            size: 11
          }
        }
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
      delay: (context) => {
        let delay = 0;
        if (context.type === 'data' && context.mode === 'default') {
          delay = context.dataIndex * 2;
        }
        return delay;
      },
    },
  };

  const drawdownOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Drawdown Analysis',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context) {
            return 'Drawdown: ' + context.parsed.y.toFixed(2) + '%';
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 8,
          font: {
            size: 11
          }
        }
      },
      y: {
        display: true,
        reverse: true,
        grid: {
          color: 'rgba(239, 68, 68, 0.1)',
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(1) + '%';
          },
          font: {
            size: 11
          }
        }
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <Card className="p-6 bg-slate-800/80 border-slate-700" data-testid="backtest-chart">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="w-6 h-6 text-blue-400" />
        <h2 className="text-2xl font-bold text-white">Backtest Results</h2>
      </div>

      {/* NAV Chart */}
      <div className="mb-8" style={{ height: '400px' }}>
        <Line ref={chartRef} data={navChartData} options={navOptions} />
      </div>

      {/* Drawdown Chart */}
      <div style={{ height: '250px' }}>
        <Line data={drawdownChartData} options={drawdownOptions} />
      </div>
    </Card>
  );
};

export default BacktestChart;
